import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnSizingState } from '@tanstack/react-table'
import { UserSettings, CalendarPreferences } from '@xeetr/types/models'
import { useCallback, useContext, useRef } from 'react'

import { UserService } from '../services/User.service'
import { showErrorToastUnlessPermissionDenied, showToast } from '../utils/toastMessage'

import { useAuth } from './useAuth'
import { ActiveTenantContext, ActiveUserContext } from '@/context/common/Context'
import { queryKeys } from '@/lib/queryKeys'
import { invalidateUserQueries } from '@/lib/mutationHelpers'

export const useUserSettings = () => {
  const { getAccessTokenSilently } = useAuth()
  const queryClient = useQueryClient()
  const { tenantId } = useContext(ActiveTenantContext)
  const { user } = useContext(ActiveUserContext)

  const { data: settings, isLoading } = useQuery({
    queryKey: queryKeys.userSettings(user?.id || ''),
    queryFn: async () => {
      const token = await getAccessTokenSilently()
      const service = new UserService(token)
      return service.getUserSettings()
    },
    enabled: !!tenantId,
    staleTime: 10 * 60 * 1000, // 10 minutes (user data changes infrequently)
    refetchIntervalInBackground: false, // Only refetch when window is focused
  })

  const mutation = useMutation({
    mutationFn: async (partialSettings: Partial<UserSettings>) => {
      const token = await getAccessTokenSilently()
      const service = new UserService(token)
      return service.patchUserSettings(partialSettings)
    },
    // update cache and invalidate other queries; toasts are shown by caller
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.userSettings(user?.id || ''), data)
      if (user) invalidateUserQueries(user.id)
    },
    onError: () => {
      // callers handle errors as needed
    },
  })

  // Helper: Get dashboard columns for current tenant
  const getDashboardColumns = useCallback((): string[] | undefined => {
    if (!tenantId || !settings?.dashboardColumns) return undefined
    return settings.dashboardColumns[tenantId]
  }, [tenantId, settings?.dashboardColumns])

  // Helper: Set dashboard columns for current tenant
  const setDashboardColumns = useCallback(
    (columns: string[]) => {
      if (!tenantId) return
      mutation.mutate(
        {
          dashboardColumns: {
            ...settings?.dashboardColumns,
            [tenantId]: columns,
          },
        },
        {
          onSuccess: () => {
            showToast({ severity: 'success', detail: 'Dashboard-Spalten gespeichert.' })
          },
          onError: (error) => {
            showErrorToastUnlessPermissionDenied(error, {
              severity: 'error',
              detail: 'Fehler beim Speichern der Dashboard-Spalten.',
            })
          },
        }
      )
    },
    [tenantId, settings?.dashboardColumns, mutation]
  )

  // Helper: Get customer columns for current tenant
  const getCustomerColumns = useCallback((): string[] | undefined => {
    if (!tenantId || !settings?.customerColumns) return undefined
    return settings.customerColumns[tenantId]
  }, [tenantId, settings?.customerColumns])

  // Helper: Set customer columns for current tenant
  const setCustomerColumns = useCallback(
    (columns: string[]) => {
      if (!tenantId) return
      mutation.mutate(
        {
          customerColumns: {
            ...settings?.customerColumns,
            [tenantId]: columns,
          },
        },
        {
          onSuccess: () => {
            showToast({ severity: 'success', detail: 'Kundenspalten gespeichert.' })
          },
          onError: (error) => {
            showErrorToastUnlessPermissionDenied(error, {
              severity: 'error',
              detail: 'Fehler beim Speichern der Kundenspalten.',
            })
          },
        }
      )
    },
    [tenantId, settings?.customerColumns, mutation]
  )

  // ---- calendar preferences helpers ----
  const getCalendarPreferences = useCallback(() => {
    if (!tenantId || !settings?.calendar) return undefined
    return settings.calendar[tenantId]
  }, [tenantId, settings?.calendar])

  type CalendarPrefsOptions = { silent?: boolean }

  const setCalendarPreferences = useCallback(
    (prefs: Partial<CalendarPreferences>, opts: CalendarPrefsOptions = {}) => {
      if (!tenantId) return
      const prev = settings?.calendar?.[tenantId] || ({} as CalendarPreferences)
      // Ensure showDaysWithoutActivities is always defined
      mutation.mutate(
        {
          calendar: {
            ...settings?.calendar,
            [tenantId]: {
              showDaysWithoutActivities:
                prefs.showDaysWithoutActivities !== undefined
                  ? prefs.showDaysWithoutActivities
                  : (prev.showDaysWithoutActivities ?? false),
              defaultView: prefs.defaultView !== undefined ? prefs.defaultView : prev.defaultView,
              timeInterval:
                prefs.timeInterval !== undefined ? prefs.timeInterval : prev.timeInterval,
              slotDuration:
                prefs.slotDuration !== undefined ? prefs.slotDuration : prev.slotDuration,
              showUtilization:
                prefs.showUtilization !== undefined ? prefs.showUtilization : prev.showUtilization,
              resourceAreaWidth:
                prefs.resourceAreaWidth !== undefined
                  ? prefs.resourceAreaWidth
                  : prev.resourceAreaWidth,
            },
          },
        },
        {
          onSuccess: (data) => {
            queryClient.setQueryData(queryKeys.userSettings(user?.id || ''), data)
            if (user) invalidateUserQueries(user.id)
            if (!opts.silent) {
              showToast({
                severity: 'success',
                detail: 'Benutzereinstellungen erfolgreich aktualisiert.',
              })
            }
          },
          onError: (error) => {
            showErrorToastUnlessPermissionDenied(error, {
              severity: 'error',
              detail: 'Fehler beim Speichern der Benutzereinstellungen.',
            })
          },
        }
      )
    },
    [tenantId, settings?.calendar, mutation, queryClient, user]
  )

  return {
    settings: settings || ({} as UserSettings),
    isLoading,
    updateSettings: mutation.mutate,
    isUpdating: mutation.isPending,

    // Tenant-specific helpers
    getDashboardColumns,
    setDashboardColumns,
    getCustomerColumns,
    setCustomerColumns,

    getCalendarPreferences,
    setCalendarPreferences,
  }
}

/**
 * Hook for managing table column sizing persistence
 * @param tableId Unique identifier for the table (e.g., 'bookings', 'customers')
 */
export const useColumnSizing = (tableId: string) => {
  const { settings, updateSettings, isLoading } = useUserSettings()
  const settingKey = `table-sizing-${tableId}` as const
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)

  const columnSizing: ColumnSizingState = (settings[settingKey] as ColumnSizingState) || {}

  const setColumnSizing = useCallback(
    (sizing: ColumnSizingState) => {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Debounce the update by 500ms
      debounceTimerRef.current = setTimeout(() => {
        updateSettings({ [settingKey]: sizing })
      }, 500)
    },
    [settingKey, updateSettings]
  )

  return {
    columnSizing,
    setColumnSizing,
    isLoading,
  }
}
